import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// The page and drawer share one cart. Keep their writes and checkout controls locked together.
let cartMutationPending = false;
let cartRecoveryFailed = false;
const checkoutSelector = '.cart__ctas, [name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
const cartMutationSelector = 'cart-items-component, cart-discount-component, product-form-component, sticky-add-to-cart, quick-order-list-component';
const cartInertStates = new Map();
const checkoutInertStates = new Map();

function setLocked(element, locked, previousStates) {
  if (locked) {
    if (!previousStates.has(element)) previousStates.set(element, element.inert);
    element.inert = true;
  } else if (previousStates.has(element)) {
    element.inert = previousStates.get(element);
    previousStates.delete(element);
  }
}

function checkoutIsBlocked() {
  return cartMutationPending || cartRecoveryFailed ||
    Boolean(document.querySelector('[data-cart-quantity-mismatch="true"]'));
}

function updateCartLocks() {
  for (const component of document.querySelectorAll(cartMutationSelector)) {
    if (!(component instanceof HTMLElement)) continue;
    setLocked(component, cartMutationPending, cartInertStates);
    component.classList.toggle('cart-items-disabled', cartMutationPending);
    component.setAttribute('aria-busy', String(cartMutationPending));
  }
  for (const control of document.querySelectorAll(checkoutSelector)) {
    if (!(control instanceof HTMLElement)) continue;
    setLocked(control, checkoutIsBlocked(), checkoutInertStates);
    control.setAttribute('aria-disabled', String(checkoutIsBlocked()));
  }
  for (const states of [cartInertStates, checkoutInertStates]) {
    for (const element of states.keys()) if (!element.isConnected) states.delete(element);
  }
}

// Capture form submissions as well as clicks: CSS pointer-events alone does not stop keyboard checkout.
for (const eventName of ['click', 'submit']) {
  window.addEventListener(eventName, (event) => {
    if (!(event.target instanceof Element)) return;
    const pendingMutation = cartMutationPending && event.target.closest(cartMutationSelector);
    const blockedCheckout = checkoutIsBlocked() && (event.target.closest(checkoutSelector) ||
      (eventName === 'submit' && event.target.matches('form.cart-form')));
    // Window capture runs before the theme's document-level delegated submit handlers,
    // including the sticky button's programmatic click on its product form.
    if (pendingMutation || blockedCheckout) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // Lock synchronously on the first quantity event; a debounce leaves a checkout race window.
  #boundOnQuantityChange = this.#onQuantityChange.bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundOnQuantityChange);
    queueMicrotask(updateCartLocks);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundOnQuantityChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (cartMutationPending) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // Keep rows visible until Shopify confirms removal, so failures never show a false empty cart.
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (cartMutationPending) return;
    const { line, quantity } = config;
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row?.dataset.key || row.dataset.parentKey || !Number.isInteger(quantity) || quantity < 0) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    cartMutationPending = true;
    updateCartLocks();
    this.refs.cartTotal?.shimmer();
    this.#showCartError('');

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate);
    let original;
    let children = [];
    let changingParent = false;
    try {
      // Read the current keys before writing. Discounts can change a line key since the last render.
      let cart = await this.#readCart();
      original = cart.items.find((item) => item.key === row.dataset.key);
      if (!original || original.parent_relationship) throw new Error('Cart line changed');
      children = cart.items.filter((item) => item.parent_relationship?.parent_key === original.key);

      // change.js validates inventory; update.js does not validate existing cart lines.
      changingParent = true;
      cart = await this.#changeLine(original.key, quantity, sections);
      changingParent = false;
      for (const child of quantity > 0 ? children : []) {
        const parent = this.#findLine(cart, original);
        if (!parent) throw new Error('Cart parent changed');
        const currentChild = this.#findLine(cart, child, parent.key);
        if (!currentChild) throw new Error('Cart child changed');
        if (currentChild.quantity !== parent.quantity) {
          cart = await this.#changeLine(currentChild.key, parent.quantity, sections);
        }
      }
      await this.#renderCart(cart, sections);
      cartRecoveryFailed = false;
      if (this.#hasQuantityMismatch(cart)) this.#showCartError(this.#errorMessage('quantitySyncError'));
    } catch (error) {
      // A lost response may follow a successful write. Never guess or roll back blindly.
      const responseConfirmed = error instanceof Error && error.name === 'CartResponseError';
      let message = responseConfirmed ? error.message : '';
      try {
        let cart = await this.#readCart();
        let recoverySyncFailed = false;
        let attemptedClampedSync = false;
        // Shopify can return 422 after clamping the frame to available stock. Only reconcile
        // this operation's known children after an explicit parent rejection and a fresh read.
        if (responseConfirmed && 'status' in error && error.status === 422 && changingParent &&
            original && quantity > 0 && children.length > 0) {
          const recoveredParent = this.#findLine(cart, original);
          if (recoveredParent && recoveredParent.quantity !== original.quantity) {
            attemptedClampedSync = true;
            try {
              for (const child of children) {
                const parent = this.#findLine(cart, original);
                if (!parent || parent.quantity !== recoveredParent.quantity) throw new Error('Cart parent changed');
                const currentChild = this.#findLine(cart, child, parent.key);
                if (!currentChild) throw new Error('Cart child changed');
                if (currentChild.quantity !== parent.quantity) {
                  cart = await this.#changeLine(currentChild.key, parent.quantity, sections);
                }
              }
            } catch {
              recoverySyncFailed = true;
              cart = await this.#readCart();
            }
          }
        }
        await this.#renderCart(cart, sections);
        // After a transport failure the original write may still finish late. Require a confirmed
        // retry (including the same quantity), removal, or page refresh before checkout.
        cartRecoveryFailed = (!responseConfirmed || recoverySyncFailed) && cart.items.length > 0;
        if (this.#hasQuantityMismatch(cart)) {
          const syncMessage = this.#errorMessage('quantitySyncError');
          message = attemptedClampedSync ? `${message} ${syncMessage}` : syncMessage;
        } else if (recoverySyncFailed) {
          message = `${message} ${this.#errorMessage('quantityUpdateError')}`;
        }
      } catch {
        cartRecoveryFailed = true;
      }
      this.#showCartError(message || this.#errorMessage('quantityUpdateError'));
    } finally {
      cartMutationPending = false;
      for (const component of document.querySelectorAll('cart-items-component')) resetShimmer(component);
      updateCartLocks();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  async #readCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Cart could not be read');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    return cart;
  }

  async #changeLine(id, quantity, sections) {
    const body = JSON.stringify({ id, quantity, sections: sections.join(','), sections_url: window.location.pathname });
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('json', { body }), signal: AbortSignal.timeout(15000),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors || !Array.isArray(cart.items)) {
      const details = [cart.description, cart.message, cart.errors].find((value) => typeof value === 'string' && value);
      const error = Object.assign(new Error(details || this.#errorMessage('quantityUpdateError')), {
        status: response.status,
      });
      // Only an explicit validation rejection is confirmed. A 5xx or malformed success may
      // arrive after a write, so recovery must retain the same lock as a lost response.
      if (response.status === 400 || response.status === 422) error.name = 'CartResponseError';
      throw error;
    }
    return cart;
  }

  #findLine(cart, original, parentKey = undefined) {
    const properties = (item) => JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
    const matches = cart.items.filter((item) =>
      item.variant_id === original.variant_id && properties(item) === properties(original) &&
      item.selling_plan_allocation?.selling_plan?.id === original.selling_plan_allocation?.selling_plan?.id &&
      item.parent_relationship?.parent_key === parentKey
    );
    return matches.find((item) => item.key === original.key) || (matches.length === 1 ? matches[0] : undefined);
  }

  #hasQuantityMismatch(cart) {
    return cart.items.some((item) => {
      const parentKey = item.parent_relationship?.parent_key;
      if (!parentKey) return false;
      const parent = cart.items.find((candidate) => candidate.key === parentKey);
      return !parent || parent.quantity !== item.quantity;
    });
  }

  async #renderCart(cart, sectionIds) {
    let sections = cart.sections;
    if (!sections || sectionIds.some((id) => typeof sections[id] !== 'string')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Cart sections could not be read');
      sections = await response.json();
    }
    if (sectionIds.some((id) => typeof sections[id] !== 'string')) throw new Error('Missing cart section');
    await Promise.all(sectionIds.map((id) => {
      const component = document.querySelector(`cart-items-component[data-section-id="${CSS.escape(id)}"]`);
      return morphSection(id, sections[id], { mode: component?.hasAttribute('data-drawer') ? 'hydration' : 'full' });
    }));
    for (const wrapper of document.querySelectorAll('[data-cart-quantity-mismatch]')) {
      wrapper.setAttribute('data-cart-quantity-mismatch', String(this.#hasQuantityMismatch(cart)));
    }
    this.#updateQuantitySelectors(cart);
    this.#updateCartQuantitySelectorButtonStates();
    document.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, {
      itemCount: cart.item_count, source: 'cart-items-component', sections,
    }));
  }

  #errorMessage(key) {
    return document.querySelector('[data-quantity-update-error]')?.dataset[key] || '';
  }

  #showCartError(message) {
    for (const notice of document.querySelectorAll('[data-cart-update-error]')) {
      notice.textContent = message;
      notice.hidden = !message;
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    // Our writes already await both page and drawer renders before broadcasting.
    if ('data' in event.detail && event.detail.data.source === 'cart-items-component') return;
    if (event instanceof DiscountUpdateEvent) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      updateCartLocks();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      await morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
    updateCartLocks();
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
